/**
 * Test Connection / Load databases results, bound to what they tested (every engine; UI review F17, round B R2).
 *
 * A result belongs to a SNAPSHOT: the dialog's connection identity (`owner` — the saved id in edit mode, the draft id
 * of a new connection, which changes after a save), the draft GENERATION (bumped whenever the engine changes) and the
 * fingerprint of every field the dial reads. It is shown only while the form still matches that snapshot. Within one
 * connection and generation, returning every tested field to the tested values shows it again — it is true for exactly
 * those values; across connections or an engine change it never comes back (owner decision, round B).
 *
 * Asynchronous completions (a test, Load databases, a submit) check `matchesLive(snapshot)` before they touch the form
 * or save, so a late answer never applies to values the user has since edited.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Connection, TestStepwiseResult } from '@/types/electron';
import type { ConnectionFormValues } from '../components/connectionFormSchema';
import type { TestLeg } from '../components/TestConnectionStepper';
import type { StorageFailureContext } from '../utils/storageTestLegs';

export type ConnectionTestResult = {
  success: boolean;
  message: string;
  /** The exact probe connection the failing test dialed — the error notice's Oracle fixes reuse its target. */
  connection?: Connection;
  /** Structured driver error code when the engine preserved one across IPC. */
  errorCode?: string;
  /**
   * 'warning' renders a calm amber notice instead of the red "Connection failed" banner: the connect itself did not
   * fail (an empty or failed database listing, a pre-connect credential problem, a refused storage verdict).
   */
  variant?: 'warning';
};

export type ConnectionTestSnapshot = { owner: string; generation: number; fingerprint: string };

/** One run's answer. A storage run carries `legs` (one row per service); an SSH run `stepwise`; the rest `result`. */
export type ConnectionTestOutcome = {
  snapshot: ConnectionTestSnapshot;
  result?: ConnectionTestResult;
  stepwise?: TestStepwiseResult;
  legs?: TestLeg[];
  /** The legs' one-line summary ("Blob verified · Table refused"; C2 N1). */
  legsSummary?: string;
  /** What an SSH or Azure Storage run dialed (the bastion, the target, the auth mode; Option C C2's result strip). */
  connection?: Connection;
  /** An Azure Storage run's failed services, structured (category, code, fix kind — never the message; C2 review C2-02). */
  storageFailures?: StorageFailureContext[];
};

/**
 * Never read by a dial. `readOnly` is read by SQLite only (the file is opened read-only) — see `testedFields`. The TNS
 * server provenance only decides what a Save records as the server; Azure Storage's "Connect using" pick only what the
 * form shows (C3).
 */
const NOT_TESTED_FIELDS: ReadonlySet<string> = new Set(['alias', 'color', 'mcpPermissionLevel', 'oracleServerFromTnsTarget', 'azureStorageModePick']);

/** What a pristine password field dials with: the saved password (the probe reads it when the field is still empty). */
const SAVED_PASSWORD = '\u0000saved-password';

function testedFields(values: Partial<ConnectionFormValues>): Array<[string, unknown]> {
  return Object.entries(values).filter(([key, value]) => {
    if (NOT_TESTED_FIELDS.has(key)) return false;
    // Only SQLite passes `readOnly` to the open call (sqliteConnectionManager); elsewhere it is an app-side guard.
    if (key === 'readOnly' && values.engine !== 'sqlite') return false;
    // An empty text field and an absent one test the same (the submit path drops empty values before testing).
    return value !== undefined && value !== '';
  });
}

/**
 * Key-order-independent JSON of the fields a connection test reads. `passwordEdited: false` (the field is pristine)
 * fingerprints the password as "the saved one", so hydrating the stored password into the field is not an edit.
 */
export function connectionTestFingerprint(
  values: Partial<ConnectionFormValues>,
  options: { passwordEdited?: boolean } = {},
): string {
  const canonical = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical((value as Record<string, unknown>)[key])]));
    }
    return value;
  };
  const tested: Record<string, unknown> = Object.fromEntries(testedFields(values));
  if (options.passwordEdited === false) tested.password = SAVED_PASSWORD;
  return JSON.stringify(canonical(tested));
}

export function sameConnectionTestSnapshot(a: ConnectionTestSnapshot, b: ConnectionTestSnapshot): boolean {
  return a.owner === b.owner && a.generation === b.generation && a.fingerprint === b.fingerprint;
}

type Options = {
  /** The dialog's connection identity (edit: the saved id; new: the draft id). */
  owner: string;
  /** The form's engine; every change starts a new generation. */
  engine: string;
  /** The fingerprint of the form as rendered (`useStore(form.store, …)`). */
  renderedFingerprint: string;
  /** The fingerprint of `values`, or of the LIVE form when omitted (read at call time, never from a render). */
  fingerprintOf: (values?: Partial<ConnectionFormValues>) => string;
};

export function useConnectionTestOutcome({ owner, engine, renderedFingerprint, fingerprintOf }: Options) {
  const [outcome, setOutcome] = useState<ConnectionTestOutcome | null>(null);
  const [dismissed, setDismissed] = useState<ConnectionTestOutcome | null>(null);
  const [generation, setGeneration] = useState(0);
  const generationRef = useRef(0);
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const fingerprintRef = useRef(fingerprintOf);
  fingerprintRef.current = fingerprintOf;

  const bumpGeneration = useCallback(() => {
    generationRef.current += 1;
    setGeneration(generationRef.current);
  }, []);
  const previousEngine = useRef(engine);
  useEffect(() => {
    if (previousEngine.current === engine) return;
    previousEngine.current = engine;
    bumpGeneration();
  }, [engine, bumpGeneration]);

  /** The snapshot of `values` (default: the live form) under the current identity and generation. */
  const snapshotOf = useCallback(
    (values?: Partial<ConnectionFormValues>): ConnectionTestSnapshot => ({
      owner: ownerRef.current,
      generation: generationRef.current,
      fingerprint: fingerprintRef.current(values),
    }),
    [],
  );
  /** Does the live form still hold exactly what `snapshot` tested? Checked by every async completion before it acts. */
  const matchesLive = useCallback((snapshot: ConnectionTestSnapshot) => sameConnectionTestSnapshot(snapshot, snapshotOf()), [snapshotOf]);
  const show = useCallback(
    (snapshot: ConnectionTestSnapshot, parts: Omit<ConnectionTestOutcome, 'snapshot'>) => setOutcome({ snapshot, ...parts }),
    [],
  );
  const clear = useCallback(() => setOutcome(null), []);
  /** A run's own edit of the form (the storage untick, a failed fix's revert): its answer now describes `to`. */
  const rebase = useCallback((from: ConnectionTestSnapshot, to: ConnectionTestSnapshot) => {
    setOutcome((previous) => (previous && previous.snapshot === from ? { ...previous, snapshot: to } : previous));
  }, []);

  /** The strip's X: hides this answer on screen only; the next answer (any new run) shows again. */
  const dismiss = useCallback(() => setDismissed(outcome), [outcome]);

  const rendered: ConnectionTestSnapshot = { owner, generation, fingerprint: renderedFingerprint };
  const visible = outcome && sameConnectionTestSnapshot(outcome.snapshot, rendered) ? outcome : null;
  /** What the strip shows: `visible` unless the user closed it. Save anyway's refusal still reads `visible`. */
  const shown = visible !== dismissed ? visible : null;
  return { visible, shown, show, clear, dismiss, rebase, snapshotOf, matchesLive, bumpGeneration };
}
